/**
 * Storage durability — the risk that decides whether this product works at all.
 *
 * Everything the user writes lives in IndexedDB on one device. Browsers evict
 * IndexedDB under storage pressure, and Safari clears it after seven days
 * without a visit unless the site is installed to the Home Screen. So the app
 * pushes on three fronts at once:
 *
 *   1. `navigator.storage.persist()` on every launch, not just the first
 *   2. Home Screen installation, which is what actually exempts iOS
 *   3. a mandatory encrypted export, which is the only one that survives a
 *      lost device
 *
 * The first two are requests the browser may refuse. The third is the one we
 * control, which is why the UI treats it as a requirement rather than a nudge.
 */

export interface PersistenceStatus {
  /** The browser has promised not to evict us. */
  persisted: boolean;
  /** Whether the browser exposes the API at all. */
  supported: boolean;
  /** Running from the Home Screen / app shell rather than a browser tab. */
  installedAsPWA: boolean;
  usageBytes: number | null;
  quotaBytes: number | null;
}

export function isInstalledAsPWA(): boolean {
  if (typeof window === 'undefined') return false;
  const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches ?? false;
  // iOS Safari predates display-mode and uses a non-standard flag.
  const iosStandalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone || iosStandalone;
}

/**
 * Asks for persistent storage. Called on every launch on purpose: Chrome grants
 * it based on engagement heuristics that change over time, so an early refusal
 * is not final and re-asking costs nothing.
 */
export async function requestPersistence(): Promise<PersistenceStatus> {
  const supported =
    typeof navigator !== 'undefined' &&
    typeof navigator.storage?.persist === 'function' &&
    typeof navigator.storage?.persisted === 'function';

  if (!supported) {
    return {
      persisted: false,
      supported: false,
      installedAsPWA: isInstalledAsPWA(),
      usageBytes: null,
      quotaBytes: null,
    };
  }

  let persisted: boolean;
  try {
    persisted = (await navigator.storage.persisted()) || (await navigator.storage.persist());
  } catch {
    persisted = false;
  }

  let usageBytes: number | null = null;
  let quotaBytes: number | null = null;
  try {
    const estimate = await navigator.storage.estimate?.();
    usageBytes = estimate?.usage ?? null;
    quotaBytes = estimate?.quota ?? null;
  } catch {
    /* estimate is optional */
  }

  return {
    persisted,
    supported: true,
    installedAsPWA: isInstalledAsPWA(),
    usageBytes,
    quotaBytes,
  };
}

/**
 * The launch heartbeat.
 *
 * `lost` is the interesting state: the server knows the user has anchors, the
 * device says it has no vault. That means eviction, a new device, or a cleared
 * profile — and it is the moment to put "restore from your export file" in
 * front of them, before the next drill fails for a reason that looks like
 * forgetting.
 */
export type LocalVaultStatus = 'ok' | 'lost' | 'unknown';

export function classifyVaultStatus(input: {
  hasLocalVault: boolean;
  serverAnchorCount: number;
}): LocalVaultStatus {
  if (input.hasLocalVault) return 'ok';
  if (input.serverAnchorCount > 0) return 'lost';
  return 'unknown';
}

/** Fires the beforeinstallprompt flow if the browser offered us one. */
export type InstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferredInstallPrompt: InstallPrompt | null = null;

export function captureInstallPrompt(): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (event: Event) => {
    event.preventDefault();
    deferredInstallPrompt = event as InstallPrompt;
  };
  window.addEventListener('beforeinstallprompt', handler);
  return () => window.removeEventListener('beforeinstallprompt', handler);
}

export function canPromptInstall(): boolean {
  return deferredInstallPrompt !== null;
}

export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredInstallPrompt) return 'unavailable';
  const prompt = deferredInstallPrompt;
  deferredInstallPrompt = null;
  await prompt.prompt();
  const choice = await prompt.userChoice;
  return choice.outcome;
}
